import { useMemo } from 'react';

import useLoggedMember from '@hooks/useLoggedMember';

import type { Meeting } from '@meetings/details/types';
import useMeetingIdFromRoute from '@meetings/hooks/useMeetingIdFromRoute';
import { type Meeting as OverviewMeeting } from '@meetings/overview/types';

import { AccessManagementPermission } from '@components/layout/AccessManagement/AccessManagement.interface';

import useMeeting from './useMeeting';

type MeetingRoleInsights =
    | 'viewer'
    | 'editor'
    | 'guest'
    | 'meeting leader'
    | 'minutes viewer'
    | 'participant';

export const getMeetingRoles = (
    meeting: Meeting | OverviewMeeting | undefined,
    loggedMemberId: string
): MeetingRoleInsights[] => {
    if (!meeting) return [];

    const member = meeting.members?.find((m) => m.id === loggedMemberId);
    const meetingLead = 'meetingLead' in meeting ? meeting.meetingLead : '';

    if (!member) return [];

    const { isGuest, isParticipant, permission } = member;

    const roleMap: Record<MeetingRoleInsights, boolean> = {
        viewer: permission === AccessManagementPermission.VIEW,
        editor: permission === AccessManagementPermission.EDIT,
        guest: Boolean(isGuest),
        'meeting leader': meetingLead === loggedMemberId,
        'minutes viewer': permission === AccessManagementPermission.VIEW_MINUTES,
        participant: Boolean(isParticipant)
    };

    return (Object.keys(roleMap) as MeetingRoleInsights[]).filter((role) => roleMap[role]);
};

const useLoggedMemberMeetingRoleInsights = (): MeetingRoleInsights[] => {
    const meetingId = useMeetingIdFromRoute();
    const { data: loggedMember } = useLoggedMember({ enabled: Boolean(meetingId) });
    const { data: meeting } = useMeeting(meetingId, { enabled: Boolean(meetingId) });

    const loggedMemberId = loggedMember?.id;

    const loggedMemberRoles = useMemo(() => {
        if (!loggedMemberId || !meetingId || !meeting) {
            return [];
        }
        return getMeetingRoles(meeting, loggedMemberId);
    }, [meetingId, meeting, loggedMemberId]);

    return loggedMemberRoles;
};

export default useLoggedMemberMeetingRoleInsights;
